import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEntityUrl, type EntitySearchResult } from '../lib/entityUrls';
import type { EntityType } from '../components/EntityChip';
import { EntityPreviewModal } from '../components/EntityPreviewModal';

// Positive allowlist of the entity types EntityPreviewModal actually knows
// how to render (its ENDPOINTS map). Anything else — including 'tag' and
// non-EntityType values like 'attachment' that can still reach this code
// at runtime (e.g. via the [[ entity-link suggestion) — falls through to
// the navigate/open-in-new-tab branch in openPreview() instead of opening
// the modal, which would throw on an unmapped endpoint lookup.
const PREVIEWABLE: EntityType[] = [
  'task', 'note', 'meeting_instance', 'meeting_series',
  'person', 'scope', 'reference_doc', 'quick_capture',
];
export function isPreviewable(type: EntityType): boolean {
  return PREVIEWABLE.includes(type);
}

interface EntityPreviewContextValue {
  openPreview: (type: EntityType, id: string) => void;
  closePreview: () => void;
}

const EntityPreviewContext = createContext<EntityPreviewContextValue | null>(null);

export function useEntityPreview(): EntityPreviewContextValue {
  const ctx = useContext(EntityPreviewContext);
  if (!ctx) throw new Error('useEntityPreview must be used within EntityPreviewProvider');
  return ctx;
}

export function EntityPreviewProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [target, setTarget] = useState<{ type: EntityType; id: string } | null>(null);

  const openPreview = useCallback((type: EntityType, id: string) => {
    if (!isPreviewable(type)) {
      const url = getEntityUrl({ entity_type: type, entity_id: id } as EntitySearchResult);
      // Some non-previewable types (e.g. 'attachment') resolve to a real
      // server download URL, not an SPA route — react-router's navigate()
      // would just hit the SPA catch-all for those. Open server URLs in a
      // new tab instead of routing through the client-side router.
      if (url.startsWith('/api/')) window.open(url, '_blank', 'noopener');
      else navigate(url);
      return;
    }
    setTarget({ type, id });
  }, [navigate]);

  const closePreview = useCallback(() => setTarget(null), []);

  return (
    <EntityPreviewContext.Provider value={{ openPreview, closePreview }}>
      {children}
      {target && <EntityPreviewModal type={target.type} id={target.id} onClose={closePreview} />}
    </EntityPreviewContext.Provider>
  );
}
